import React, {useEffect, useState, useRef} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useNavigate, useLocation} from 'react-router-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  getUserAccountInfo,
  setIsUserLanguageReloading,
  switchCustomer,
  emulateUser,
  getDatabaseUserNames,
} from '../../appSlice';
import {clearCabviewUiCache} from '../../features/Cabview/cabviewUiLocalCache';

import {updateSearchText} from './searchBarSlice';
import {DownStatusIcon} from '../DownStatusIcon';
import {sendGAEvent, fetchPostAuthSafe} from '../../app/utils';
import {GlobalFilterModal} from '../GlobalFilterModal';
import {
  Button,
  Badge,
  MenuItem,
  Menu,
  Select,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Box,
  Typography,
  Divider,
  ListItemIcon,
  MenuList,
} from '@mui/material';
import ChatToggleButton from '../ChatPanel/ChatToggleButton';
import {SearchableSelectTable} from '../SearchableSelectTable';

const LANGUAGE_NAMES = {en: 'English', es: 'Espa\u00f1ol'};

const languageFlags = {
  en: '/img/usa-flag-circle.png',
  es: '/img/mexico-flag-circle.png',
};

// English shows the UK flag instead of the US flag for users whose units are set to metric
function getLanguageFlags(unitsLength) {
  return unitsLength === 'metric' ? {...languageFlags, en: '/img/uk-flag-circle.png'} : languageFlags;
}

function Topbar(props) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const displaySettings = useSelector((state) => {
    return state.framework;
  });
  const customerSettings = useSelector((state) => {
    return state.app.customerSettings;
  });
  const userSettings = useSelector((state) => {
    return state.app.userSettings;
  });
  const searchText = useSelector((state) => {
    return state.searchBar.searchText;
  });
  const appFilters = useSelector((state) => {
    return state.app.appFilters;
  });
  const vehicles = useSelector((state) => {
    return state.app.vehicles;
  });
  const isUserLanguageSwitchAvailable = useSelector((state) => {
    return state.app.isUserLanguageSwitchAvailable;
  });
  const isUserLanguageReloading = useSelector((state) => {
    return state.app.isUserLanguageReloading;
  });
  const smallScreen = useSelector((state) => {
    return state.framework.smallScreen;
  });
  const authenticationStatus = useSelector((state) => {
    return state.app.authenticationStatus;
  });
  const customerName = useSelector((state) => {
    return state.app.customerName;
  });
  const databaseUserNames = useSelector((state) => {
    return state.app.databaseUserNames;
  });

  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [languageDropdownAnchor, setLanguageDropdownAnchor] = useState(null);
  const [localSearchText, setLocalSearchText] = useState(searchText);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);
  const [customerSwitching, setCustomerSwitching] = useState('');
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [pendingCustomerName, setPendingCustomerName] = useState('');
  const [emulateModalOpen, setEmulateModalOpen] = useState(false);
  const [emulateSelecting, setEmulateSelecting] = useState('');
  const [accountActionError, setAccountActionError] = useState('');
  const searchDebounceRef = useRef(null);
  const customerSelectLastClickRef = useRef(0);

  const flags = getLanguageFlags(userSettings.general?.unitsLength);

  const accountMenuOpen = Boolean(accountMenuAnchor);

  const accountMenuPaperSx = {
    borderRadius: '16px',
    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    paddingBottom: '0.25rem',
  };

  // Only populated by the backend for users permitted to switch, so no role check is needed here
  const customerSwitcherOptions = userSettings.customerOptions ?? [];
  const emulatedUserName = userSettings.emulatedUserName ?? '';

  useEffect(() => {
    // Focus on dropdown input when opened
    $('.dropdown').on('shown.bs.dropdown', function () {
      $('.dropdown-menu input').focus();
    });
  }, []);

  // Keep local input state in sync with global search text (e.g. when route changes)
  useEffect(() => {
    setLocalSearchText(searchText);
  }, [searchText]);

  // Debounced dispatch for global search text to avoid heavy recomputes on every key stroke
  const handleSearchInputChange = (value) => {
    setLocalSearchText(value);
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      dispatch(updateSearchText(value));
    }, 150);
  };

  const handleChangeLanguage = async (newLanguage) => {
    if (userSettings.language != newLanguage) {
      dispatch(setIsUserLanguageReloading(true));
      const updateDoc = {
        language: newLanguage,
      };

      const options = {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(updateDoc),
      };
      await fetchPostAuthSafe(
        '/settings/updateUserLanguage',
        options,
        userSettings.username,
        userSettings.databaseName
      );

      // Fetch and update user data
      dispatch(getUserAccountInfo());
    }
  };

  function languageSelect() {
    if (isUserLanguageReloading) {
      return <CircularProgress />;
    }
    return (
      <Select
        title='Language'
        size='small'
        className='mt-1 ml-2'
        value={userSettings.language}
        onChange={(event) => {
          return handleChangeLanguage(event.target.value);
        }}
        renderValue={(selected) => {
          return (
            <span>
              <img className='mb-1' src={flags[selected]} style={{width: 24, height: 24}} />
              {smallScreen ? '' : ` ${selected.toUpperCase()}`}
            </span>
          );
        }}
        sx={{
          '& .MuiSelect-select': {
            paddingLeft: 1,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            paddingX: '0.25rem',
            border: 'none',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
        }}
      >
        <MenuItem value='en'>
          <span>
            <img src={flags['en']} style={{width: 24, height: 24}} /> English
          </span>
        </MenuItem>
        <MenuItem value='es'>
          <span>
            <img src={flags['es']} style={{width: 24, height: 24}} /> Español
          </span>
        </MenuItem>
      </Select>
    );
  }

  // Get count of filters
  const allVehInFilter = [];
  if (typeof appFilters.vehicles !== 'undefined') {
    appFilters.vehicles.forEach((vehicleSN) => {
      allVehInFilter.push(vehicleSN);
    });
  }
  if (typeof appFilters.vehicleLabels !== 'undefined') {
    appFilters.vehicleLabels.forEach((labelId) => {
      vehicles.forEach((vehicle) => {
        if (vehicle.machineType == labelId || vehicle.labels.includes(labelId)) {
          allVehInFilter.push(vehicle.serialNumber);
        }
      });
    });
  }
  const uniqueVehInFilter = [...new Set(allVehInFilter)].length;

  return (
    <React.Fragment>
      {/* Filter modal */}
      <GlobalFilterModal open={filtersModalOpen} setFiltersModalOpen={setFiltersModalOpen}></GlobalFilterModal>

      {/* Topbar */}
      <nav className='navbar navbar-expand topbar static-top shadow w-100 px-1'>
        <button id='sidebarToggleTop' className='btn btn-link d-md-none' onClick={openSidebar}>
          <FontAwesomeIcon icon='fa fa-bars' style={{color: 'black'}} />
        </button>
        <div
          className='d-sm-flex align-items-center justify-content-between mr-auto ml-md-2 my-2 my-md-0 mw-100'
          style={{gap: '12px'}}
        >
          <h1 className={`${smallScreen ? 'h4' : 'h3'} mb-0 text-gray-800`}>{props.text}</h1>
          {props.badge}
        </div>

        {/* Language button */}
        {isUserLanguageSwitchAvailable && languageSelect()}

        {/* Filter button */}
        {userSettings.general?.globalFiltersEnabled && location.pathname != '/statsview' && (
          <Badge
            badgeContent={uniqueVehInFilter}
            max={9999}
            showZero={uniqueVehInFilter > 0}
            overlap='circular'
            color='error'
          >
            <Button
              title='App Filters'
              sx={{minWidth: 'auto', paddingX: '0.25rem'}}
              onClick={() => {
                setFiltersModalOpen(true);
              }}
            >
              <FontAwesomeIcon
                icon='fas fa-filter'
                size={smallScreen ? 'xl' : '2x'}
                style={{color: uniqueVehInFilter ? '#4e73df' : '#858697'}}
              />
            </Button>
          </Badge>
        )}

        {/* Down status */}
        {userSettings.general?.globalFiltersEnabled && location.pathname != '/inspectionForm' && (
          <DownStatusIcon login={false} small={false} />
        )}

        {/* Search */}
        {props.search && (
          <React.Fragment>
            <div
              className='dropdown dropdown-fw dropdown-fw-backdrop'
              style={{display: !displaySettings.smallScreen && 'none'}}
            >
              <button className='btn p-0' data-toggle='dropdown' aria-expanded='false'>
                <FontAwesomeIcon
                  icon='fas fa-search'
                  size={smallScreen ? 'xl' : '2x'}
                  style={{color: localSearchText && '#4e73df'}}
                />
              </button>
              <div className='dropdown-menu dropdown-menu-right'>
                <input
                  type='search'
                  className='dropdown-item border form-control'
                  placeholder='Search..'
                  value={localSearchText}
                  onChange={(e) => {
                    return handleSearchInputChange(e.target.value);
                  }}
                  onFocus={() => {
                    sendGAEvent('search_focus', 'SearchBarMobile', 'search');
                  }}
                />
              </div>
            </div>

            <input
              type='search'
              className='form-control w-25 mx-1'
              placeholder='Search..'
              value={localSearchText}
              onChange={(e) => {
                return handleSearchInputChange(e.target.value);
              }}
              onFocus={() => {
                sendGAEvent('search_focus', 'SearchBarDesktop', 'search');
              }}
              style={{...(displaySettings.smallScreen ? {display: 'none'} : {})}}
            />
          </React.Fragment>
        )}

        {/* Customer switcher. On mobile this moves into the account menu, where there is room. */}
        {!smallScreen && customerSwitcherOptions.length > 1 && <div className='mx-1'>{customerSelect()}</div>}
        {/* Insights assistant in top bar (hidden on smallScreen); ChatToggleButton hides on /insights and when panel open */}
        {!smallScreen && authenticationStatus?.aiChatEnabled && <ChatToggleButton />}

        {/* Account menu. Anchored dropdown on desktop, centered modal on mobile. */}
        <button
          className='btn px-1'
          id='dropdownMenuButton'
          aria-label='Account menu'
          onClick={(event) => {
            return setAccountMenuAnchor(event.currentTarget);
          }}
        >
          <FontAwesomeIcon icon='fas fa-user-circle' size={smallScreen ? 'xl' : '2x'} />
        </button>
        {smallScreen ? (
          <Dialog
            open={accountMenuOpen}
            onClose={closeAccountMenu}
            fullWidth
            maxWidth='xs'
            PaperProps={{sx: accountMenuPaperSx}}
          >
            <MenuList disablePadding>{accountMenuContent()}</MenuList>
          </Dialog>
        ) : (
          <Menu
            anchorEl={accountMenuAnchor}
            open={accountMenuOpen}
            onClose={closeAccountMenu}
            autoFocus={false}
            disableAutoFocusItem
            anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
            transformOrigin={{vertical: 'top', horizontal: 'right'}}
            PaperProps={{sx: {...accountMenuPaperSx, minWidth: '17rem', maxWidth: '22rem', marginTop: '0.5rem'}}}
          >
            {accountMenuContent()}
          </Menu>
        )}
      </nav>

      <Dialog
        open={customerModalOpen}
        fullWidth
        maxWidth='sm'
        onClose={() => {
          if (customerSwitching) {
            return;
          }
          closeCustomerModal();
        }}
      >
        <DialogTitle>Switch Customer</DialogTitle>
        <DialogContent>
          <SearchableSelectTable
            items={customerSwitcherOptions.map((customerOption) => {
              return {
                key: customerOption.name,
                label: customerOption.organizationName,
                subLabel: customerOption.name,
              };
            })}
            activeKey={pendingCustomerName || customerName}
            onSelect={(selectedCustomerName) => {
              if (customerSwitching) {
                return;
              }
              setPendingCustomerName(selectedCustomerName);
            }}
            searchPlaceholder='Search customers...'
            countNoun='customers'
          />
        </DialogContent>
        <DialogActions>
          <Button
            disabled={customerSwitching !== ''}
            onClick={() => {
              return closeCustomerModal();
            }}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            disabled={customerSwitching !== '' || !pendingCustomerName || pendingCustomerName === customerName}
            onClick={() => {
              return customerSwitchClick(pendingCustomerName);
            }}
            startIcon={customerSwitching !== '' ? <CircularProgress size={16} color='inherit' /> : null}
          >
            Switch
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={emulateModalOpen}
        fullWidth
        maxWidth='sm'
        onClose={() => {
          return setEmulateModalOpen(false);
        }}
      >
        <DialogTitle>Emulate User</DialogTitle>
        <DialogContent>
          <Alert severity='info' className='mb-2'>
            View the app with another user&apos;s settings applied. Anything you change is still recorded against your
            own account.
          </Alert>
          <SearchableSelectTable
            items={Object.values(databaseUserNames || {})
              .map((databaseUser) => {
                return {
                  key: databaseUser.username,
                  label: databaseUser.name || databaseUser.username,
                  subLabel: databaseUser.username,
                };
              })
              .sort((a, b) => {
                return a.label.localeCompare(b.label);
              })}
            activeKey={emulatedUserName}
            selecting={emulateSelecting}
            onSelect={emulateUserClick}
            searchPlaceholder='Search users...'
            countNoun='users'
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              return setEmulateModalOpen(false);
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={accountActionError !== ''}
        autoHideDuration={6000}
        anchorOrigin={{vertical: 'top', horizontal: 'center'}}
        onClose={() => {
          return setAccountActionError('');
        }}
      >
        <Alert
          severity='warning'
          onClose={() => {
            return setAccountActionError('');
          }}
        >
          {accountActionError}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );

  // Handle click on logout button
  function logoutClick() {
    dispatch({type: 'RESET'}); // Need to reset the states to clear current cache
    clearCabviewUiCache(); // Cabview's localStorage cache isn't per-account; wipe it so the next login can't inherit it
    navigate('/login');
    fetch('/logout')
      .then((response) => {
        window.location.href = '/login'; // Use window.location to cause a new page to load
      })
      .catch((error) => {
        console.log(error);
      });
  }

  // Rendered inside a Menu on desktop and a Dialog on mobile.
  // An array, not a Fragment: MUI Menu rejects Fragment as a direct child.
  function accountMenuContent() {
    return [
      <Box key='account-identity' sx={{paddingX: 2, paddingTop: 1.5, paddingBottom: 1}}>
        <Typography variant='subtitle1' sx={{fontWeight: 600, lineHeight: 1.2}}>
          {userSettings.username}
        </Typography>
        {/* On mobile the switcher replace the customerName text display if it's available, since the bar has no room for it */}
        {smallScreen && customerSwitcherOptions.length > 1 ? (
          customerSelect()
        ) : (
          <Typography variant='body2' color='text.secondary' sx={{wordBreak: 'break-word'}}>
            {customerSettings.general?.organizationName}
          </Typography>
        )}
      </Box>,
      emulatedUserName && (
        <Box key='emulate-banner' sx={{paddingX: 2, paddingBottom: 1}}>
          <Alert
            severity='warning'
            sx={{paddingY: 0, '& .MuiAlert-message': {paddingY: 0.5}}}
            action={
              <Button
                size='small'
                disabled={emulateSelecting !== ''}
                onClick={() => {
                  return emulateUserClick('');
                }}
              >
                Stop
              </Button>
            }
          >
            Viewing as {emulatedUserName}
          </Alert>
        </Box>
      ),
      <Divider key='account-menu-div-identity' />,
      userSettings.canEmulateUser && !emulatedUserName && (
        <MenuItem key='emulate-user' onClick={openEmulateModal}>
          <ListItemIcon>
            <FontAwesomeIcon icon='fas fa-user-secret' />
          </ListItemIcon>
          Emulate User
        </MenuItem>
      ),
      // Language lives here rather than beside the search box, so it is reachable from every
      // page instead of only the handful that set isUserLanguageSwitchAvailable. Shows the
      // active language with a flag and opens the same picker Shopview uses.
      <Box key='language-picker' sx={{px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1}}>
        <FontAwesomeIcon icon='fas fa-language' style={{width: 20}} />
        <Select
          size='small'
          fullWidth
          value={userSettings.language || 'en'}
          disabled={isUserLanguageReloading}
          onChange={(event) => {
            return handleChangeLanguage(event.target.value);
          }}
          renderValue={(selected) => {
            return (
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <img src={flags[selected]} alt='' style={{width: 20, height: 20}} />
                <span>{LANGUAGE_NAMES[selected] || selected.toUpperCase()}</span>
              </Box>
            );
          }}
          sx={{'fontSize': '0.85rem', '.MuiSelect-select': {py: 0.5}}}
        >
          {Object.keys(LANGUAGE_NAMES).map((code) => {
            return (
              <MenuItem key={code} value={code} sx={{fontSize: '0.85rem'}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                  <img src={flags[code]} alt='' style={{width: 20, height: 20}} />
                  <span>{LANGUAGE_NAMES[code]}</span>
                </Box>
              </MenuItem>
            );
          })}
        </Select>
        {isUserLanguageReloading && <CircularProgress size={16} />}
      </Box>,
      <MenuItem
        key='personal-settings'
        onClick={() => {
          closeAccountMenu();
          navigate('/personal_settings');
        }}
      >
        <ListItemIcon>
          <FontAwesomeIcon icon='fas fa-gear' />
        </ListItemIcon>
        Personal Settings
      </MenuItem>,
      checkOrganizationSettingsAccess() && (
        <MenuItem
          key='org-settings'
          onClick={() => {
            closeAccountMenu();
            navigate('/organization_settings');
          }}
        >
          <ListItemIcon>
            <FontAwesomeIcon icon='fas fa-building' />
          </ListItemIcon>
          Organization Settings
        </MenuItem>
      ),
      <Divider key='account-menu-div-logout' />,
      <MenuItem key='logout' id='main-logout-button' onClick={logoutClick} sx={{color: 'error.main'}}>
        <ListItemIcon sx={{color: 'error.main'}}>
          <FontAwesomeIcon icon='fas fa-right-from-bracket' />
        </ListItemIcon>
        Logout
      </MenuItem>,
    ].filter(Boolean);
  }

  function closeAccountMenu() {
    setAccountMenuAnchor(null);
  }

  // Subtle text trigger, shared by the top bar (desktop) and the account menu (mobile)
  function customerSelect() {
    const organizationLabel = customerSettings.general?.organizationName || customerName;
    return (
      <Typography
        variant='body2'
        color='text.secondary'
        disabled={customerSwitching !== ''}
        onClick={handleCustomerSelectClick}
        sx={{
          appearance: 'none',
          WebkitAppearance: 'none',
          border: 'none',
          background: 'none',
          padding: 0,
          margin: 0,
          cursor: 'default',
          userSelect: 'none',
          textAlign: 'left',
          maxWidth: '14rem',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          '&:hover': {
            color: customerSwitching !== '' ? undefined : 'text.primary',
          },
          '&:disabled': {
            opacity: 0.6,
          },
        }}
      >
        {organizationLabel}
      </Typography>
    );
  }

  function handleCustomerSelectClick() {
    if (customerSwitching !== '') {
      return;
    }

    const now = Date.now();
    const intervalMs = customerSelectLastClickRef.current ? now - customerSelectLastClickRef.current : null;
    customerSelectLastClickRef.current = now;

    if (intervalMs !== null && intervalMs < 300) {
      customerSelectLastClickRef.current = 0;
      openCustomerModal();
    }
  }

  function openCustomerModal() {
    closeAccountMenu();
    setAccountActionError('');
    setPendingCustomerName(customerName);
    setCustomerModalOpen(true);
  }

  function closeCustomerModal() {
    setCustomerModalOpen(false);
    setPendingCustomerName('');
  }

  // Switch to another customer. A switch is a soft re-login, so the app is reloaded on success.
  async function customerSwitchClick(selectedCustomerName) {
    if (customerSwitching || selectedCustomerName === customerName) {
      return;
    }

    setCustomerSwitching(selectedCustomerName);
    setAccountActionError('');
    sendGAEvent('customer_switch', 'Topbar', 'account');

    const result = await dispatch(switchCustomer(selectedCustomerName, userSettings.username, customerName));

    if (!result.success) {
      setCustomerSwitching('');
      setAccountActionError(result.errorMsg || 'Could not switch customer');
      return;
    }

    window.location.href = '/';
  }

  function openEmulateModal() {
    closeAccountMenu();
    setAccountActionError('');
    dispatch(getDatabaseUserNames());
    setEmulateModalOpen(true);
  }

  // Start emulating a user, or stop when passed an empty username. Reloads so every view refetches
  // against the emulated settings.
  async function emulateUserClick(targetUserName) {
    if (emulateSelecting) {
      return;
    }

    setEmulateSelecting(targetUserName || 'stop');
    setAccountActionError('');
    sendGAEvent(targetUserName ? 'emulate_user_start' : 'emulate_user_stop', 'Topbar', 'account');

    const result = await dispatch(emulateUser(targetUserName, userSettings.username, customerName));

    if (!result.success) {
      setEmulateSelecting('');
      setAccountActionError(result.errorMsg || 'Could not change emulation');
      return;
    }

    window.location.href = '/';
  }

  function openSidebar() {
    props.handleSetSideMenuOpened(true);
  }

  function checkOrganizationSettingsAccess() {
    const organizationSettingsAccess = Object.keys(userSettings.roleViewAccess).some((key) => {
      return userSettings.roleViewAccess[key];
    });

    // Organization settings are only available for accounts with geotab access
    return organizationSettingsAccess;
  }
}

export {Topbar};
